import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { useState } from "react";
import { BookOpen, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";

interface Section {
  id: string;
  title: string;
  content: React.ReactNode;
}

function SectionCard({ section }: { section: Section }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" id={section.id}>
      <button
        className="w-full text-left p-6 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <h3 className="text-base font-bold text-slate-900">{section.title}</h3>
        {expanded ? <ChevronUp className="h-5 w-5 text-slate-400 shrink-0" /> : <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />}
      </button>
      {expanded && (
        <div className="px-6 pb-6 border-t border-slate-100 pt-4">
          {section.content}
        </div>
      )}
    </div>
  );
}

export default function PharmacistNMP() {
  const sections: Section[] = [
    {
      id: "rps-framework",
      title: "RPS Competency Framework for All Prescribers (2021)",
      content: (
        <div className="space-y-4">
          <p className="text-sm text-slate-700 leading-relaxed">
            The Royal Pharmaceutical Society's <strong>A Competency Framework for All Prescribers</strong> (2021) defines the standards for safe and effective prescribing for all independent prescribers, including pharmacists. It is the recognised standard against which pharmacist independent prescribers should assess and develop their prescribing practice.
          </p>
          <p className="text-sm text-slate-600">The framework is organised into <strong>10 competencies</strong> across two domains:</p>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
              <h4 className="text-sm font-bold text-violet-900 mb-3">Domain 1: The Consultation</h4>
              <ol className="space-y-2 text-sm text-violet-800">
                {[
                  "Assess the patient",
                  "Consider the options",
                  "Reach a shared decision",
                  "Prescribe",
                  "Provide information",
                  "Monitor and review",
                  "Prescribe safely",
                  "Prescribe professionally",
                ].map((c, i) => (
                  <li key={c} className="flex items-start gap-2">
                    <span className="font-bold text-violet-500 shrink-0">{i + 1}.</span> {c}
                  </li>
                ))}
            </ol>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h4 className="text-sm font-bold text-blue-900 mb-3">Domain 2: Prescribing Governance</h4>
              <ol start={9} className="space-y-2 text-sm text-blue-800">
                {[
                  "Improve prescribing practice",
                  "Prescribe as part of a team",
                ].map((c, i) => (
                  <li key={c} className="flex items-start gap-2">
                    <span className="font-bold text-blue-500 shrink-0">{i + 9}.</span> {c}
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-sm font-semibold text-slate-700 mb-1">Key Principles</p>
            <ul className="space-y-1 text-sm text-slate-700">
              <li className="flex items-start gap-2"><span className="text-violet-400 shrink-0">•</span>Prescribing must be evidence-based and within the prescriber's competence</li>
              <li className="flex items-start gap-2"><span className="text-violet-400 shrink-0">•</span>Patient safety is the primary consideration in all prescribing decisions</li>
              <li className="flex items-start gap-2"><span className="text-violet-400 shrink-0">•</span>Pharmacist prescribers must practise within their clinical area of competence</li>
              <li className="flex items-start gap-2"><span className="text-violet-400 shrink-0">•</span>Continuing professional development is required to maintain prescribing competence</li>
              <li className="flex items-start gap-2"><span className="text-violet-400 shrink-0">•</span>Prescribers must have appropriate clinical supervision and governance support</li>
            </ul>
            <a
              href="https://www.rpharms.com/resources/frameworks/prescribing-competency-framework"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-violet-700 hover:text-violet-900 mt-3 font-medium"
            >
              <ExternalLink className="h-3.5 w-3.5" /> RPS Prescribing Competency Framework (External)
            </a>
          </div>
        </div>
      ),
    },
    {
      id: "scope",
      title: "Scope of Practice in Rheumatology — What a Pharmacist Independent Prescriber Can Do",
      content: (
        <div className="space-y-4">
          <p className="text-sm text-slate-700 leading-relaxed">
            A pharmacist who has qualified as an Independent Prescriber (IP) can prescribe any licensed medicine (including Schedule 2–5 controlled drugs) within their competence, for any condition. In the context of rheumatology, this enables significant clinical contribution.
          </p>

          <div className="space-y-3">
            <h4 className="text-sm font-bold text-slate-800">Typical Rheumatology NMP Pharmacist Roles Include:</h4>
            {[
              {
                area: "DMARD Initiation and Optimisation",
                detail: "Initiating and adjusting methotrexate, hydroxychloroquine, sulfasalazine, leflunomide, azathioprine, and mycophenolate. Writing prescriptions following shared decision-making with the patient and within agreed clinical protocols.",
              },
              {
                area: "Biologic and JAK Inhibitor Prescribing",
                detail: "Where competence has been established, prescribing biologic DMARDs (TNFi, IL-6i, IL-17i, IL-23i, rituximab, abatacept, belimumab) and targeted synthetic DMARDs (JAK inhibitors) within NICE TA criteria and trust formulary.",
              },
              {
                area: "Monitoring-Triggered Dose Adjustment",
                detail: "Acting on abnormal monitoring results — adjusting or temporarily suspending doses of DMARDs based on FBC, LFT, and renal function results within agreed clinical protocols.",
              },
              {
                area: "Medicines Reconciliation",
                detail: "Identifying and resolving drug interactions, duplicate therapies, and adherence issues. Prescribing symptomatic treatments (NSAIDs, analgesics, gastroprotection) within competence.",
              },
              {
                area: "Steroid Prescribing and Tapering",
                detail: "Prescribing prednisolone courses and tapering regimens (e.g. for flares, bridging therapy), within an agreed clinical framework.",
              },
              {
                area: "Shared Care Protocol Management",
                detail: "Supporting shared care prescribing agreements between hospital rheumatology and primary care GPs — including monitoring schedule communication, dose adjustment guidance, and escalation pathways.",
              },
            ].map((item) => (
              <div key={item.area} className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-sm font-bold text-slate-900 mb-1">{item.area}</p>
                <p className="text-sm text-slate-700">{item.detail}</p>
              </div>
            ))}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm font-bold text-amber-800 mb-1">Scope Limitation Reminder</p>
            <p className="text-sm text-amber-900">A pharmacist independent prescriber must only prescribe within their demonstrated area of competence. If a clinical situation falls outside your competence, escalate to the responsible consultant or specialist nurse. Prescribing outside competence is an ethical and regulatory breach under GPhC Standards.</p>
          </div>
        </div>
      ),
    },
    {
      id: "controlled-drugs",
      title: "Controlled Drug Considerations",
      content: (
        <div className="space-y-4">
          <p className="text-sm text-slate-700 leading-relaxed">
            Pharmacist Independent Prescribers may prescribe Schedule 2–5 Controlled Drugs (CDs) for any medical condition within their competence. In rheumatology, this is most relevant to:
          </p>

          <div className="space-y-3">
            {[
              {
                drug: "Opioid Analgesics (Schedule 2)",
                drugs: "Morphine, oxycodone, codeine, dihydrocodeine, tramadol",
                context: "Pain management in refractory musculoskeletal pain, palliative management of severe arthritis, or short-term post-procedural pain. PIPs should follow NICE NG193 (Chronic pain) — opioids are not first-line for non-malignant chronic pain.",
                requirements: "FP10 prescription required with standard CD prescription requirements. Morphine/oxycodone: max 30-day supply. Dose, form, and strength must be written in full. Prescriber must not dispense CDs they have prescribed (in most community settings — check local policy).",
              },
              {
                drug: "Gabapentinoids (Schedule 3)",
                drugs: "Pregabalin, gabapentin",
                context: "Neuropathic pain components in inflammatory arthritis, fibromyalgia, and radiculopathy.",
                requirements: "FP10MDA (or FP10) required. Standard CD schedule 3 requirements apply.",
              },
              {
                drug: "Benzodiazepines / Z-drugs (Schedule 4)",
                drugs: "Diazepam (muscle spasm), temazepam, zopiclone, zolpidem",
                context: "Rarely required in rheumatology — only for acute severe muscle spasm. Follow NICE guidance on benzodiazepine prescribing and prescribe for minimum durations.",
                requirements: "FP10 prescription. Standard requirements. Maximum 30-day supply.",
              },
            ].map((item) => (
              <div key={item.drug} className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-sm font-bold text-slate-900 mb-0.5">{item.drug}</p>
                <p className="text-xs text-slate-500 mb-2">Includes: {item.drugs}</p>
                <p className="text-sm text-slate-700 mb-2"><span className="font-medium">Context:</span> {item.context}</p>
                <p className="text-sm text-slate-700"><span className="font-medium">Requirements:</span> {item.requirements}</p>
              </div>
            ))}
          </div>

          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-sm font-bold text-red-800 mb-1">Key Reminders for CD Prescribing</p>
            <ul className="space-y-1 text-sm text-red-900">
              <li>• CD prescriptions must include: full name and address of patient; date; drug name, form, strength, dose and frequency; total quantity in words AND figures</li>
              <li>• Check for existing CD prescriptions from other prescribers (Controlled Drug database where available)</li>
              <li>• Consider controlled drug dependence and diversion risks — prescribe lowest effective dose for shortest duration</li>
              <li>• Emergency supply of CDs has restricted rules — follow GPHC and MHRA guidance</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "prescription-standards",
      title: "Prescription Writing Standards (BNF/NHSE)",
      content: (
        <div className="space-y-4">
          <p className="text-sm text-slate-700 leading-relaxed">
            All prescriptions written by a pharmacist independent prescriber must meet the legal and professional requirements for a valid prescription.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <h4 className="text-sm font-bold text-slate-800 mb-2">FP10 Prescription Requirements</h4>
              <ul className="space-y-1.5 text-sm text-slate-700">
                {[
                  "Patient's full name, address, and date of birth",
                  "Date of prescribing",
                  "Drug name (generic prescribing preferred by NHSE)",
                  "Formulation and strength (e.g. tablets, 200mg)",
                  "Dose and frequency (e.g. take 1 tablet twice daily)",
                  "Quantity or course duration",
                  "Prescriber's name, address, and signature",
                  "Prescriber's GPhC number",
                ].map((req) => (
                  <li key={req} className="flex items-start gap-2">
                    <span className="text-violet-400 shrink-0 mt-0.5">✓</span>
                    {req}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-50 rounded-xl p-4">
              <h4 className="text-sm font-bold text-slate-800 mb-2">Prescribing Best Practice</h4>
              <ul className="space-y-1.5 text-sm text-slate-700">
                {[
                  "Prescribe generically unless specific brand is clinically necessary (e.g. narrow therapeutic index drugs, modified-release formulations)",
                  "Use BNF recommended drug names and strengths",
                  "Avoid ambiguous dose instructions — be specific",
                  "Include 'cautionary and advisory labels' where relevant (BNF Appendix 3)",
                  "Note known allergy status on prescription where relevant",
                  "Sign and date every prescription — do not pre-sign blanks",
                  "Retain a copy/entry in patient records for all prescriptions",
                ].map((req) => (
                  <li key={req} className="flex items-start gap-2">
                    <span className="text-violet-400 shrink-0 mt-0.5">✓</span>
                    {req}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h4 className="text-sm font-bold text-blue-800 mb-2 flex items-center gap-2">
              <BookOpen className="h-4 w-4" /> Key Reference Documents
            </h4>
            <ul className="space-y-1.5 text-sm text-blue-900">
              {[
                { text: "BNF Prescribing Guidance", href: "https://bnf.nice.org.uk/about/how-to-use-bnf/" },
                { text: "GPhC Standards for Pharmacy Professionals", href: "https://www.pharmacyregulation.org/standards/standards-for-pharmacy-professionals" },
                { text: "RPS Prescribing Competency Framework 2021", href: "https://www.rpharms.com/resources/frameworks/prescribing-competency-framework" },
                { text: "NHSE Medicines Optimisation — Prescribing Support", href: "https://www.england.nhs.uk/medicines-2/medicines-optimisation/" },
                { text: "MHRA — Prescribing Controlled Drugs", href: "https://www.gov.uk/government/publications/controlled-drugs-list-of-most-commonly-used-drugs" },
              ].map((link) => (
                <li key={link.text}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 hover:text-blue-700"
                  >
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "governance",
      title: "Governance, Clinical Supervision & GPhC Standards",
      content: (
        <div className="space-y-4">
          <p className="text-sm text-slate-700 leading-relaxed">
            Pharmacist Independent Prescribers operate under GPhC Standards for Pharmacy Professionals and must work within appropriate governance structures.
          </p>

          <div className="space-y-3">
            {[
              {
                title: "GPhC Registration",
                detail: "All pharmacist prescribers must be on the GPhC register with an independent prescribing annotation. The annotation is recorded on the GPhC register — patients and employers can verify it.",
              },
              {
                title: "Clinical Supervision",
                detail: "All prescribing practitioners should have access to clinical supervision, peer support, and a named senior clinician to escalate to when faced with clinical uncertainty. In rheumatology, this is typically the responsible consultant rheumatologist. Supervision arrangements should be documented in the clinical governance framework.",
              },
              {
                title: "Prescribing Governance Framework",
                detail: "Prescribing should occur within a documented governance framework, including: clinical protocols or patient group directions, trust formulary, indemnity cover, prescribing authority scope, audit and incident reporting, and CPD requirements. Check your employer's PGD/protocol framework.",
              },
              {
                title: "Indemnity",
                detail: "Pharmacist independent prescribers require professional indemnity. Employer-provided indemnity (via NHS Resolution for NHS staff) typically covers prescribing within the employment role. Check that prescribing is explicitly included. Personal professional indemnity may be needed for additional private practice.",
              },
              {
                title: "CPD and Revalidation",
                detail: "GPhC revalidation requires 9 CPD records per year, of which at least 4 must involve peer review. Prescribers should ensure CPD specifically addresses prescribing competence, including at least one reflective account related to a prescribing event annually. GPhC CPD record-keeping must demonstrate ongoing learning in the area of prescribing practice.",
              },
              {
                title: "Audit and Quality Improvement",
                detail: "All prescribers should regularly audit their prescribing practice. Identify patterns of prescribing errors, near-misses, and outcomes data. Participate in trust medicines governance meetings and incident reviews. Document audit activity as part of GPhC CPD.",
              },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-sm font-bold text-slate-900 mb-1">{item.title}</p>
                <p className="text-sm text-slate-700">{item.detail}</p>
              </div>
            ))}
          </div>
        </div>
      ),
    },
  ];

  return (
    <PharmacistLayout title="Non-Medical Prescribing">
      <div className="space-y-6">
        <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 text-sm text-violet-800">
          <strong>Reference hub for Pharmacist Independent Prescribers (PIPs).</strong> Covers the RPS Competency Framework (2021), rheumatology scope of practice, controlled drug prescribing, and GPhC governance requirements. Always check current guidance — prescribing law can change.
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-800 mb-3 text-sm">Quick Navigation</h3>
          <div className="flex flex-wrap gap-2">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="text-xs bg-violet-50 text-violet-700 border border-violet-200 px-3 py-1.5 rounded-full hover:bg-violet-100 transition-colors"
              >
                {s.title.split("—")[0].split("(")[0].trim()}
              </a>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {sections.map((section) => (
            <SectionCard key={section.id} section={section} />
          ))}
        </div>
      </div>
    </PharmacistLayout>
  );
}
